import { useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Handle, Position } from "reactflow";
import { loadVideoScene, loadSceneImage, saveGame } from "../../redux/asyncThunk/userActions";
import "reactflow/dist/style.css";
import "../index.css";
import { userSlice } from "../../redux/user/userSlice";
import NodeDeleteContext from "./NodeDeleteContext";
import { sanitizePlainText } from "../../utils/inputValidation";
import {
  getButtonStyle,
  getButtonPosition,
  stripSchemeTransient,
  getNextTrainerButtonSlot,
} from "./schemeLayout";
import SchemeSceneContent from "./SchemeSceneContent";
import { useSceneScreenshot } from "./useSceneScreenshot";
import { getMaxButtonsForGame, isTrainerGame } from "../../utils/contentType";
import {
  buildQuestionModePatch,
  buildScenarioModePatch,
  inferScenarioContentMode,
  normalizeContentMode,
} from "../../utils/contentMode";

const { addMessage } = userSlice;

export function NodeWithToolbar(props) {
  const { data, isConnectable, id } = props;
  const { nodes, edges } = useContext(NodeDeleteContext);
  const [buttonText, setButtonText] = useState("");
  const { hasScreenshot, hasImagePreview, imageSrc, imagePreviewSrc, imageKey, refreshScreenshot } =
    useSceneScreenshot(id, data);

  const dispatch = useDispatch();
  const selectedGameId = useSelector((state) => state.user?.selectedGameId);
  const game = useSelector((state) => state.user?.game);
  const user = useSelector((state) => state.user?.data?.user);
  const isTrainer = isTrainerGame(game);
  const maxButtons = getMaxButtonsForGame(game);

  const buttonNodes = (game?.nodes ?? []).filter(
    (el) => el.id.split("-")[0] === id && el.type === "input"
  );

  const persistSceneData = (patch) => {
    const updatedNodes = (game?.nodes ?? []).map((node) =>
      node.id === id
        ? { ...node, data: { ...node.data, ...patch } }
        : node
    );

    dispatch(
      saveGame({
        selectedGameId,
        nodes: stripSchemeTransient(updatedNodes),
        edges: edges ?? game?.edges ?? [],
      })
    );
  };

  const handleVideoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    dispatch(
      loadVideoScene({
        userId: user.id,
        selectedGameId,
        scene: String(id),
        file,
        game,
      })
    ).then(() => {
      refreshScreenshot();
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    dispatch(
      loadSceneImage({
        userId: user.id,
        selectedGameId,
        scene: String(id),
        file,
        game,
      })
    ).then(() => {
      refreshScreenshot();
    });
  };

  const sceneContentMode = isTrainer
    ? normalizeContentMode(data?.questionMode, "text")
    : inferScenarioContentMode(data);
  const sceneContentText = isTrainer
    ? data?.questionText ?? ""
    : data?.sceneText ?? data?.questionText ?? "";

  const handleContentModeChange = (mode) => {
    const patch = isTrainer ? buildQuestionModePatch(mode) : buildScenarioModePatch(mode);
    persistSceneData(patch);
  };

  const handleAddButtonNode = () => {
    if (!buttonText) {
      return dispatch(
        addMessage({
          type: "error",
          message: "Кнопка не может быть без текста",
        })
      );
    }

    if (buttonNodes.length >= maxButtons) {
      return dispatch(
        addMessage({
          type: "error",
          message: isTrainer
            ? "Можно добавить не более четырёх вариантов ответа"
            : "Сейчас вы можете добавить только две кнопки",
        })
      );
    }

    const slot = isTrainer
      ? getNextTrainerButtonSlot(buttonNodes)
      : buttonNodes.length && buttonNodes[0].data.buttonPosiiton === "top"
        ? "bottom"
        : "top";

    if (!slot) return;

    const checkButtonsInNode = buttonNodes[0];
    const newId = !checkButtonsInNode
      ? `${id}-${Number(id) + 1}`
      : `${id}-${Number(checkButtonsInNode.id.split("-")[1]) + 1}`;

    const newButtonNode = {
      id: newId,
      data: {
        label: buttonText,
        buttonPosiiton: slot,
        isCorrect: false,
        explanation: "",
      },
      position: getButtonPosition(slot),
      className: "scheme-button",
      style: getButtonStyle(),
      parentId: `${id}-group`,
      sourcePosition: "right",
      type: "input",
      extent: "parent",
      parentNode: `${id}-group`,
      draggable: false,
    };

    setButtonText("");
    dispatch(
      saveGame({
        selectedGameId,
        nodes: stripSchemeTransient([...nodes, newButtonNode]),
        edges,
      })
    );
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        className="scheme-scene__target-handle"
        isConnectable={isConnectable}
      />
      <SchemeSceneContent
        parentGroupId={`${id}-group`}
        hasScreenshot={hasScreenshot}
        imageKey={imageKey}
        imageSrc={imageSrc}
        hasImagePreview={hasImagePreview}
        imagePreviewSrc={imagePreviewSrc}
        buttonCount={buttonNodes.length}
        maxButtons={maxButtons}
        isTrainer={isTrainer}
        contentMode={sceneContentMode}
        contentText={sceneContentText}
        textPanelTitle={isTrainer ? "Текст вопроса" : "Текст сцены"}
        textPlaceholder={
          isTrainer ? "Введите вопрос для тренажёра" : "Введите текст сцены"
        }
        onContentModeChange={handleContentModeChange}
        onContentTextChange={(value) => {
          if (isTrainer) {
            persistSceneData({ questionText: sanitizePlainText(value, 1000) });
            return;
          }
          persistSceneData({ sceneText: sanitizePlainText(value, 2000) });
        }}
        onVideoSelected={handleVideoChange}
        onImageSelected={handleImageChange}
        onAddButton={handleAddButtonNode}
        buttonText={buttonText}
        onButtonTextChange={(value) =>
          setButtonText(sanitizePlainText(value, 100))
        }
      />
    </>
  );
}
